import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdClose, MdAdd, MdVisibility } from 'react-icons/md';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/format';


export default function Admins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'create' or 'edit'
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'reporter' });
  const [deletingId, setDeletingId] = useState(null);

  // Reporter View Stats & Articles State
  const [viewModal, setViewModal] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [reporterStats, setReporterStats] = useState(null);
  const [daySummary, setDaySummary] = useState([]);
  const [reporterArticles, setReporterArticles] = useState([]);
  const [articlesPagination, setArticlesPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchReporterStats = async (reporterId) => {
    try {
      const res = await api.get(`/admin/admins/${reporterId}/stats`);
      setReporterStats(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch reporter statistics');
    }
  };

  const fetchReporterArticles = async (reporterId) => {
    setStatsLoading(true);
    try {
      const res = await api.get('/admin/articles', {
        params: {
          admin_id: reporterId,
          status: 'published',
          limit: 10000
        }
      });
      const allArticles = res.data.data;

      const dateGroups = {};
      allArticles.forEach(art => {
        if (!art.published_at) return;
        const d = new Date(art.published_at);
        const normalizedDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const timeKey = normalizedDate.getTime();
        dateGroups[timeKey] = (dateGroups[timeKey] || 0) + 1;
      });

      const sortedGroups = Object.keys(dateGroups)
        .map(time => ({
          time: Number(time),
          dateStr: formatDate(new Date(Number(time))),
          count: dateGroups[time]
        }))
        .sort((a, b) => b.time - a.time);

      setDaySummary(sortedGroups);
      setReporterArticles(sortedGroups.slice(0, 10));
      setArticlesPagination({ page: 1, pages: Math.ceil(sortedGroups.length / 10), total: sortedGroups.length });
    } catch (err) {
      toast.error('Failed to fetch reporter articles');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleView = (admin) => {
    setViewModal(admin);
    setReporterStats(null);
    setReporterArticles([]);
    setDaySummary([]);
    setArticlesPagination({ page: 1, pages: 1, total: 0 });
    fetchReporterStats(admin.id);
    fetchReporterArticles(admin.id);
  };

  const handlePageChange = (newPage) => {
    setArticlesPagination(prev => ({ ...prev, page: newPage }));
    setReporterArticles(daySummary.slice((newPage - 1) * 10, newPage * 10));
  };

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const openCreate = () => {
    setFormData({ name: '', email: '', password: '', role: 'reporter' });
    setModal('create');
  };

  const openEdit = (admin) => {
    setFormData({ id: admin.id, name: admin.name, email: admin.email, password: '', role: admin.role });
    setModal('edit');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modal === 'create') {
        if (!formData.password) {
          return toast.error('Password is required for new accounts');
        }
        await api.post('/admin/admins', formData);
        toast.success('Admin/Reporter created');
      } else {
        await api.put(`/admin/admins/${formData.id}`, formData);
        toast.success('Account updated');
      }
      setModal(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const [deleteModal, setDeleteModal] = useState(null);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeletingId(deleteModal.id);
    try {
      await api.delete(`/admin/admins/${deleteModal.id}`);
      toast.success('Account deleted');
      setDeleteModal(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Manage Admins & Reporters</h2>
        <button className="btn btn-primary" onClick={openCreate}><MdAdd /> Add New</button>
      </div>

      <div className="table-wrap mt-md">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => (
              <tr key={admin.id}>
                <td>{admin.name}</td>
                <td>{admin.email}</td>
                <td>
                  <span className={`badge ${admin.role === 'admin' ? 'badge-live' : 'badge-category'}`}>
                    {admin.role.toUpperCase()}
                  </span>
                </td>
                <td>
                  <div className="action-btns">
                    {/* <button className="btn btn-sm btn-ghost" onClick={() => handleView(admin)} title="View Details"><MdVisibility /></button> */}
                    <button className="btn btn-sm btn-ghost" onClick={() => handleView(admin)} title="View Details"><MdVisibility /></button>
                    <button className="btn btn-sm btn-ghost" onClick={() => openEdit(admin)}><MdEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(admin)} disabled={deletingId === admin.id}><MdDelete /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Create Account' : 'Edit Account'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <form className="modal-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} required>
                    <option value="admin">Admin</option>
                    <option value="reporter">Reporter</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{modal === 'edit' ? 'Password (leave blank to keep current)' : 'Password'}</label>
                  <input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} minLength={6} />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reporter Details: {viewModal.name}</h3>
              <button className="modal-close" onClick={() => setViewModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {statsLoading && !reporterStats ? (
                <div className="text-center py-md">Loading stats...</div>
              ) : (
                <>
                  <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px', display: 'grid' }}>
                    <div className="stat-card" style={{ padding: '15px', background: '#DC262615', color: '#DC2626', borderRadius: '8px', border: '1px solid #DC262620' }}>
                      <div className="stat-info">
                        <h3 style={{ fontSize: '24px', margin: '0 0 5px 0' }}>{reporterStats?.totalArticles || 0}</h3>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>Total Articles</p>
                      </div>
                    </div>
                    <div className="stat-card" style={{ padding: '15px', background: '#F59E0B15', color: '#F59E0B', borderRadius: '8px', border: '1px solid #F59E0B20' }}>
                      <div className="stat-info">
                        <h3 style={{ fontSize: '24px', margin: '0 0 5px 0' }}>{reporterStats?.articlesToday || 0}</h3>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>Articles Today</p>
                      </div>
                    </div>
                  </div>

                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '10px' }}>Articles Created Summary</h3>

                  {statsLoading ? (
                    <div className="text-center py-md">Loading summary...</div>
                  ) : (
                    <>
                      <div className="table-wrap">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Article Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reporterArticles.map(day => (
                              <tr key={day.time}>
                                <td style={{ fontWeight: 500 }}>{day.dateStr}</td>
                                <td>{day.count}</td>
                              </tr>
                            ))}
                            {reporterArticles.length === 0 && (
                              <tr>
                                <td colSpan="2" className="empty-row" style={{ textAlign: 'center', padding: '25px', color: '#666' }}>
                                  No articles found for this reporter.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination pagination={articlesPagination} onPageChange={handlePageChange} />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Account</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this account?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>{deleteModal.name} ({deleteModal.email})</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deletingId === deleteModal.id}>
                {deletingId === deleteModal.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
